import { useCallback, useEffect, useState } from 'react';
import { getApiBaseUrl } from '../../../core/config/api';
import {
  type PlanningPersonnelRow,
  DEFAULT_PLANNING_NOM,
  effectiveWeekDays,
} from './operationalPlanningHelpers';

type TranslateFn = (key: string) => string;

/**
 * Hook extrait de OperationalPlanningModal.tsx :
 * - chargement liste personnel
 * - sauvegarde d'un jour de semaine pour un personnel
 */
export function useOperationalPlanningData(isOpen: boolean, t: TranslateFn) {
  const [rows, setRows] = useState<PlanningPersonnelRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [persistError, setPersistError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true);
    setError(null);
    setPersistError(null);
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/buildings/personnel`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success || !Array.isArray(data.data)) {
        setError(data.error || t('operationalPlanning.loadError'));
        setRows([]);
        return;
      }
      setRows(data.data as PlanningPersonnelRow[]);
    } catch {
      setError(t('operationalPlanning.networkError'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const persistWeekDay = useCallback(
    async (
      person: PlanningPersonnelRow,
      dayIndex: number,
      statut: string,
      weekKey: string,
      prevWeekKey: string
    ) => {
      const token = localStorage.getItem('token');
      if (!token) return;
      const days = [...effectiveWeekDays(person, weekKey, prevWeekKey)];
      days[dayIndex] = statut.trim() || DEFAULT_PLANNING_NOM;
      setSavingId(person.id);
      setPersistError(null);
      try {
        const res = await fetch(
          `${getApiBaseUrl(true)}/buildings/personnel/${person.id}/planning-week`,
          {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ weekStart: weekKey, days }),
          }
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success || !data.data) {
          setPersistError(data.error || t('operationalPlanning.saveError'));
          return;
        }
        const upd = data.data as PlanningPersonnelRow;
        setRows((prev) =>
          prev.map((r) =>
            Number(r.id) === Number(upd.id)
              ? {
                  ...r,
                  planning_statut_nom: upd.planning_statut_nom ?? r.planning_statut_nom,
                  planning_weeks_json: upd.planning_weeks_json ?? r.planning_weeks_json,
                }
              : r
          )
        );
      } catch {
        setPersistError(t('operationalPlanning.networkError'));
      } finally {
        setSavingId(null);
      }
    },
    [t]
  );

  return {
    rows,
    loading,
    error,
    savingId,
    persistError,
    setPersistError,
    load,
    persistWeekDay,
  };
}
